import { Button, Card, IconButton, Text, VSpacer } from '@/components/DesignSystem';
import { SaveSubwalletButton } from "@/components/shared/Subwallet/SaveSubwalletButton";
import { ToastMessages } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { LoyaltyPointsCapProgress } from '@/pages/Promotions/LoyaltyPointsCapProgress';
import { PromotionStatusChip } from '@/pages/Promotions/PromotionStatusChip';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { DetailedApiError } from '@/utilities/api/DetailedApiError';
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { getPromotionStatus, PromotionStatus } from '@/utilities/api/PromotionUtilities';
import { ApiPromotion } from '@api/interfaces/ApiPromotion';
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import { Stack, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

interface PromotionCardProps {
  isInternal?: boolean,
  onDelete: () => void,
  onEdit: () => void,
  onView: () => void,
  promotion: ApiPromotion,
  readonly?: boolean,
  testID: string,
}

export const PromotionCard = ({
  isInternal,
  onDelete,
  onEdit,
  onView,
  promotion,
  readonly,
  testID,
}: PromotionCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const promotionStatus = getPromotionStatus(promotion);
  const isFuture = promotionStatus === PromotionStatus.FUTURE;
  const isEditable = promotionStatus !== PromotionStatus.EXPIRED;
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { mutateAsync: updatePromotionSubwallet } = useMutation(
    async ({
      promotionId,
      subwalletId,
    } : { promotionId: string, subwalletId: string }) => {
      return PromotionApi.update(promotionId, { subwalletId });
    },
  );

  async function onSaveSubwallet (promotionId: string, subwalletId: string) {
    try {
      await updatePromotionSubwallet({ promotionId, subwalletId });
      await queryClient.invalidateQueries([QueryKeys.GET_PROMOTIONS]);
      openSnackbar(ToastMessages.promotionUpdated);
    } catch (err) {
      if (err instanceof DetailedApiError) {
        openSnackbar(err.message);
      }
    }
  }

  const CardContent = (
    <Stack
      alignItems='top'
      direction='row'
      justifyContent={'space-between'}
    >
      <Stack>
        {!!promotion.manufacturer?.name && (
          <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
            {promotion.manufacturer.name}
          </Text>
        )}
        <VSpacer size="1" />
        <Text category="body-xlarge">
          {promotion.name}
        </Text>
        <VSpacer size="1" />
        <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
          {t(
            $ => $.promotions.card.runs,
            {
              startDate: promotion.startDate,
              endDate: promotion.endDate,
            },
          )}
        </Text>
      </Stack>
    </Stack>
  );

  return (
    <Card
      elevation={4}
      sx={{ cursor: 'pointer' }}
      testID={testID}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between" onClick={onView}>
        <Stack>
          {CardContent}
          {!!promotion.pointCap && (
            <>
              <VSpacer size="3" />
              <LoyaltyPointsCapProgress promotion={promotion} />
            </>
          )}
          <VSpacer size="2" />
          <PromotionStatusChip promotion={promotion} />
        </Stack>
        <Stack>
          <Stack alignItems="top" direction="row" onClick={(e) => e.stopPropagation()} spacing={2}>
            <SaveSubwalletButton
              onSaveSubwallet={async (subwalletId) => {
                await onSaveSubwallet(promotion.id, subwalletId);
              }}
              promotion={promotion}
            />
            {(!readonly && isFuture) && (
              <IconButton
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                testID={`${testID}-delete-button`}
              >
                <DeleteOutline />
              </IconButton>
            )}
            <Button
              onClick={onView}
              testID={`promotion-card-details-button-${promotion.id}`}
              variant="outlined"
            >
              {t($ => $.common.actions.details)}
            </Button>
            {(isInternal && isEditable) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                startIcon={<Edit />}
                testID={`promotion-card-edit-button-${promotion.id}`}
                variant="outlined"
              >
                {t($ => $.common.actions.edit)}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
